import moment from 'moment';
require('moment/locale/nl');

export const SERVER_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const SERVER_DAY_FORMAT = 'YYYY-MM-DD';
export const DEFAULT_DATE_FORMAT = 'D-M-YYYY';
export const DEFAULT_TABLE_FORMAT = 'DD\u2009/\u2009MM\u2009/\u2009YYYY';

/* Checks that the input can be formatted correctly */
const checkTime = fn => (date, format, ...params) => { return moment(date, format).isValid() ? fn(date, format, ...params) : ''; };
const required = label => { throw (`You must provide '${label}' to use this function`); };

const formatDate = checkTime((date, from = SERVER_DATE_FORMAT, to = DEFAULT_DATE_FORMAT) => {
  return moment(date, from).format(to);
});

const serverDate = checkTime((date) => {
  return moment(date).format(SERVER_DATE_FORMAT);
});

const serverDay = checkTime((date) => {
  return moment(date).format(SERVER_DAY_FORMAT);
});

/* Computed times */
const now = () => {
  return moment().format(SERVER_DATE_FORMAT);
};

const today = () => {
  return moment().format(SERVER_DAY_FORMAT);
};

const tomorrow = () => {
  return addTime(today(), 1, 'd').format(SERVER_DAY_FORMAT);
};

const addTime = (time = required('time'), ...rest) => {
  return moment(time).add(...rest);
};

const weekday = () => {
  return moment().isoWeekday();
};

const weekdayName = (weekdayNumber = this.weekday()) => {
  return moment().isoWeekday(weekdayNumber).format('dddd');
};

const minutesSinceMidnight = () => {
  const now = moment();
  const midnight = now.clone().startOf('day');
  return now.diff(midnight, 'minutes');
};

const dayAndMinutes = (day, minutes) => {
  return moment(day).startOf('day').add('m', minutes).format(SERVER_DATE_FORMAT);
};

/* Pretty formats */
const compactDate = checkTime((date, format = SERVER_DATE_FORMAT) => {
  return moment(date, format).format('D MMM [’]YY');
});

const compactDayAndMonth = checkTime((date, format = SERVER_DATE_FORMAT) => {
  return moment(date, format).format('D MMM');
});

const prettyDate = checkTime((date, format = SERVER_DATE_FORMAT) => {
  return moment(date, format).format('LL');
});

const tableDate = checkTime((date, format = SERVER_DATE_FORMAT) => {
  return moment(date, format).format(DEFAULT_TABLE_FORMAT);
});

/* Partial times */
const time = checkTime((date, format = SERVER_DATE_FORMAT) => {
  return moment(date, format).format('HH:mm');
});

const timeFromMinutes = (minutes) => {
  return moment().startOf('day').minutes(minutes).format('HH:mm');
};

const year = checkTime((date, format = SERVER_DATE_FORMAT) => {
  return moment(date, format).format('YYYY');
});

/* Durations */
const durationMinutes = (seconds = required('seconds')) => {
  return moment.duration(seconds, 'seconds').minutes();
};

/* Ellapsed time */
const secondsSince = checkTime((date, format = SERVER_DATE_FORMAT) => {
  return moment().diff(moment(date, format), 'seconds');
});

const minutesSince = checkTime((date, format = SERVER_DATE_FORMAT) => {
  return moment().diff(moment(date, format), 'minutes');
});

const hoursSince = checkTime((date, format = SERVER_DATE_FORMAT) => {
  return moment().diff(moment(date, format), 'hours');
});

/*
    Gives the number of calendar days compared to now.
    e.g. it doesn't wait for 24 hours to pass, but only the day to change (at night)
 */
const daysSince = checkTime((date, format = SERVER_DATE_FORMAT) => {
  return moment().startOf('day').diff(moment(date, format).startOf('day'), 'days');
});

const yearsSince = checkTime((date, format = SERVER_DATE_FORMAT) => {
  return moment().diff(moment(date, format), 'years');
});

const timeSince = checkTime((date, format = SERVER_DATE_FORMAT) => {
  return moment(date, format).toNow(true);
});

const formattedDuration = checkTime((seconds) => {
  const duration = moment.duration(seconds, 'seconds');
  const h = duration.hours();
  const m = duration.minutes();
  const s = duration.seconds();
  let result = '';
  if (h > 0) {
    result += `${h}h `;
  }
  if (m > 0) {
    result += `${m}m `;
  }
  result += `${s}s`;
  return result;
});

const maximum = (...dates) => {
  return moment.max(dates.filter(d => !!d).map(d => moment(d))).format(SERVER_DATE_FORMAT);
};

const minimum = (...dates) => {
  return moment.min(dates.filter(d => !!d).map(d => moment(d))).format(SERVER_DATE_FORMAT);
};

export {
  now,
  today,
  tomorrow,
  weekday,
  weekdayName,
  minutesSinceMidnight,
  addTime,
  time,
  year,
  formatDate,
  serverDate,
  serverDay,
  durationMinutes,
  compactDate,
  compactDayAndMonth,
  prettyDate,
  tableDate,
  formattedDuration,
  secondsSince,
  minutesSince,
  hoursSince,
  daysSince,
  yearsSince,
  timeSince,
  timeFromMinutes,
  maximum,
  minimum,
  dayAndMinutes
};
